import { useEffect, useState } from 'react';
import { createPatientDocument, updatePatientDocument } from './service';
import {
  FileTextOutlined,
  DeleteOutlined,
  WarningOutlined,
  LoadingOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { Input, Modal, Tooltip } from 'antd';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';

const FileDocument = ({
  schedulepage,
  document,
  fileToUpload,
  onUpdate,
  onDelete,
  encounterId,
  appointmentUuid,
  source,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  const [hasError, setHasError] = useState(false);
  const [description, setDescription] = useState(document.description);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);

  useEffect(() => {
    if (fileToUpload) {
      setHasError(false);
      setIsLoading(true);
      createPatientDocument(encounterId, appointmentUuid, {
        type: 'file',
        file: fileToUpload,
        size: fileToUpload.size,
        source,
      })
        .then((response) => {
          onUpdate({ document: response, fileToUpload: null });
        })
        .catch(() => {
          setHasError(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [appointmentUuid, encounterId, fileToUpload, onUpdate, source]);

  const updateDescription = async () => {
    setIsLoading(true);
    try {
      const updatedDocument = await updatePatientDocument(
        document.patient_document_id,
        {
          description,
        }
      );
      onUpdate({ document: updatedDocument });
      setShowDescriptionModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  const prefixIcon = isLoading ? (
    <LoadingOutlined />
  ) : hasError ? (
    <WarningOutlined />
  ) : (
    <FileTextOutlined />
  );
  return (
    <div className="flex flex-col items-center group rounded-lg max-w-[160px]">
      {showDescriptionModal && (
        <Modal
          centered
          open
          onCancel={() => setShowDescriptionModal(false)}
          footer={
            <div className="flex justify-end gap-2">
              <button
                className="ih-button-negative left-0 mr-auto flex gap-2"
                onClick={() => {
                  analytics.track(
                    `Deleted File In ${
                      source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
                    } History Edit Description Modal`,
                    {
                      schedulepage,
                      email: user?.email,
                    }
                  );
                  onDelete();
                }}
              >
                <DeleteOutlined />
                Delete
              </button>
              <button
                className="ih-button-secondary"
                onClick={() => {
                  analytics.track(
                    `Clicked Cancel In ${
                      source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
                    } History Edit Description Modal`,
                    {
                      schedulepage,
                      email: user?.email,
                    }
                  );
                  setShowDescriptionModal(false);
                }}
              >
                Cancel
              </button>
              <button
                className="ih-button-primary"
                onClick={() => {
                  analytics.track(
                    `Clicked Save In ${
                      source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
                    } History Edit Description Modal`,
                    {
                      schedulepage,
                      email: user?.email,
                    }
                  );
                  updateDescription();
                }}
                disabled={isLoading}
              >
                Save
              </button>
            </div>
          }
          title="Edit Description"
        >
          <Input.TextArea
            autoFocus
            style={{ resize: 'none' }}
            rows={4}
            defaultValue={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Modal>
      )}
      <div
        className="relative cursor-pointer"
        onClick={() => setShowDescriptionModal(true)}
      >
        <span className={`text-[40px] ${hasError ? 'text-red-500' : ''}`}>
          {prefixIcon}
        </span>
        <div className="absolute -bottom-1 p-1 flex gap-2 justify-center left-1/2 -translate-x-1/2">
          <Tooltip title="Edit Description">
            <button
              disabled={isLoading || hasError}
              className="flex rounded-full p-1 bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-50"
              aria-label="Edit Description"
            >
              <EditOutlined className="text-md text-gray-600" />
            </button>
          </Tooltip>
          <Tooltip title="Delete">
            <button
              disabled={isLoading}
              className="flex rounded-full p-1 bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-50"
              onClick={(e) => {
                analytics.track(
                  `Deleted File In ${
                    source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
                  } History`,
                  {
                    schedulepage,
                    email: user?.email,
                  }
                );
                e.stopPropagation();
                onDelete();
              }}
              aria-label="Delete file"
            >
              <DeleteOutlined className="text-md text-gray-600" />
            </button>
          </Tooltip>
        </div>
      </div>
      <div className="w-full text-center">
        <p
          className={`text-xs font-medium text-gray-900 truncate px-1 ${
            hasError && !isLoading ? 'text-red-500' : ''
          }`}
          title={document.name}
        >
          {document.name}
        </p>
        <p className="text-xs text-gray-500">
          {((document.size || 0) / 1000).toFixed(2)} KB
        </p>
      </div>
    </div>
  );
};

export default FileDocument;
